import { useState, useMemo, useEffect, useRef } from 'react';
import {
  useExpenseCategories,
  useExpenses,
  useMonthlyFinancialSummary,
  useMonthlyCashFlow,
  useYearlyFinancialSummary,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useToggleExpensePaid,
  useCopyRecurringExpenses,
  useProductCostBreakdown,
  useSalesBreakdown,
  useWithdrawalBreakdown,
  useWithdrawalsDetail,
} from '@/hooks/useFinance';
import {
  FixedExpense,
  FixedExpenseCreate,
  MONTH_NAMES,
  CATEGORY_COLORS,
} from '@/services/finance';
import { PAYMENT_METHOD_LABELS } from '@/services/cashRegister';
import { formatPrice } from '@/utils/format';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  Home,
  Zap,
  Wifi,
  Sparkles,
  Users,
  FileText,
  Shield,
  Wrench,
  MoreHorizontal,
  PiggyBank,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import clsx from 'clsx';

// Labels para el detalle de salidas de caja
const WITHDRAWAL_CAT_LABELS: Record<string, string> = {
  delivery: 'Cadete/Envío', transport: 'Transporte', supplies: 'Compras/Insumos',
  services: 'Servicios', withdrawal: 'Retiro de Dinero', supplier_payment: 'Pago a Proveedor',
  salary: 'Sueldo', other: 'Otro',
};
const WITHDRAWAL_PAY_LABELS: Record<string, string> = {
  cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', qr: 'QR', other: 'Otro',
};

// Mapeo de iconos para categorías
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Home: Home,
  Zap: Zap,
  Wifi: Wifi,
  Sparkles: Sparkles,
  Users: Users,
  FileText: FileText,
  Shield: Shield,
  Wrench: Wrench,
  MoreHorizontal: MoreHorizontal,
};

export default function Finances() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<FixedExpense | null>(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState<number | null>(null);

  // Modals para detalle del resumen anual
  const [showYearlyDetailModal, setShowYearlyDetailModal] = useState<'ventas' | 'costo' | 'gastos' | 'resultado' | null>(null);

  // Form state
  const [formCategoryId, setFormCategoryId] = useState<string>('');
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formIsRecurring, setFormIsRecurring] = useState(false);
  const [formNotes, setFormNotes] = useState('');

  // Queries
  const { data: categories = [] } = useExpenseCategories();
  const { data: expenses = [], isLoading: loadingExpenses } = useExpenses({
    month: selectedMonth,
    year: selectedYear,
  });
  const { data: summary, isLoading: loadingSummary } = useMonthlyFinancialSummary(
    selectedYear,
    selectedMonth
  );
  const { data: cashFlow } = useMonthlyCashFlow(selectedYear, selectedMonth);
  const { data: withdrawalBreakdown } = useWithdrawalBreakdown(selectedYear, selectedMonth);
  const { data: withdrawalsDetail } = useWithdrawalsDetail(selectedYear, selectedMonth);
  const { data: yearlySummary, isLoading: loadingYearly, error: yearlyError } = useYearlyFinancialSummary(selectedYear);

  // Queries para desgloses detallados (se activan cuando el modal está abierto)
  const { data: productCostBreakdown, isLoading: loadingProductCost } = useProductCostBreakdown(
    selectedYear,
    undefined,
    showYearlyDetailModal === 'costo'
  );
  const { data: salesBreakdown, isLoading: loadingSales } = useSalesBreakdown(
    selectedYear,
    undefined,
    showYearlyDetailModal === 'ventas'
  );

  // Debug: mostrar en consola si hay error
  if (yearlyError) {
    console.error('Error cargando resumen anual:', yearlyError);
  }

  // Mutations
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();
  const togglePaid = useToggleExpensePaid();
  const copyRecurring = useCopyRecurringExpenses();

  // Track which month/year we already tried to auto-copy
  const autoCopiedRef = useRef<string | null>(null);

  // Auto-copy recurring expenses when viewing a new month with no expenses
  useEffect(() => {
    const monthKey = `${selectedYear}-${selectedMonth}`;

    // Skip if still loading, already have expenses, or already tried this month
    if (loadingExpenses || expenses.length > 0 || autoCopiedRef.current === monthKey) {
      return;
    }

    // Skip if mutation is already in progress
    if (copyRecurring.isPending) {
      return;
    }

    // Calculate previous month
    let sourceMonth = selectedMonth - 1;
    let sourceYear = selectedYear;
    if (sourceMonth === 0) {
      sourceMonth = 12;
      sourceYear = selectedYear - 1;
    }

    // Mark as tried for this month
    autoCopiedRef.current = monthKey;

    // Auto-copy recurring expenses from previous month
    copyRecurring.mutate({
      source_month: sourceMonth,
      source_year: sourceYear,
      target_month: selectedMonth,
      target_year: selectedYear,
    });
  }, [selectedMonth, selectedYear, loadingExpenses, expenses.length, copyRecurring]);

  // Navigation
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(now.getMonth() + 1);
    setSelectedYear(now.getFullYear());
  };

  // Form handlers
  const resetForm = () => {
    setFormCategoryId('');
    setFormDescription('');
    setFormAmount('');
    setFormIsRecurring(false);
    setFormNotes('');
    setEditingExpense(null);
  };

  const openNewExpenseModal = () => {
    resetForm();
    setShowExpenseModal(true);
  };

  const openEditExpenseModal = (expense: FixedExpense) => {
    setEditingExpense(expense);
    setFormCategoryId(expense.category_id?.toString() || '');
    setFormDescription(expense.description || '');
    setFormAmount(formatAmountForDisplay(expense.amount.toString()));
    setFormIsRecurring(expense.is_recurring);
    setFormNotes(expense.notes || '');
    setShowExpenseModal(true);
  };

  // Helper functions para formateo de montos
  const formatAmountForDisplay = (value: string): string => {
    // Convierte "1234.56" a "1.234,56"
    const num = parseFloat(value.replace(/\./g, '').replace(',', '.'));
    if (isNaN(num)) return '';
    return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseAmountFromDisplay = (value: string): string => {
    // Convierte "1.234,56" a "1234.56" para enviar al backend
    return value.replace(/\./g, '').replace(',', '.');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Solo permitir números, puntos y comas
    value = value.replace(/[^0-9.,]/g, '');
    setFormAmount(value);
  };

  const handleAmountBlur = () => {
    // Formatear al salir del campo
    if (formAmount) {
      const parsed = parseFloat(parseAmountFromDisplay(formAmount));
      if (!isNaN(parsed)) {
        setFormAmount(formatAmountForDisplay(parsed.toString()));
      }
    }
  };

  const handleSaveExpense = async () => {
    // Solo requerir monto (descripción es opcional)
    if (!formAmount) return;

    const parsedAmount = parseFloat(parseAmountFromDisplay(formAmount));
    if (isNaN(parsedAmount) || parsedAmount <= 0) return;

    const data: FixedExpenseCreate = {
      category_id: formCategoryId ? parseInt(formCategoryId) : undefined,
      description: formDescription || undefined,
      amount: parsedAmount,
      month: selectedMonth,
      year: selectedYear,
      is_recurring: formIsRecurring,
      notes: formNotes || undefined,
    };

    try {
      if (editingExpense) {
        await updateExpense.mutateAsync({
          id: editingExpense.id,
          data,
        });
      } else {
        await createExpense.mutateAsync(data);
      }
      setShowExpenseModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteExpenseId) return;
    try {
      await deleteExpense.mutateAsync(deleteExpenseId);
      setShowDeleteModal(false);
      setDeleteExpenseId(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleTogglePaid = async (id: number) => {
    try {
      await togglePaid.mutateAsync(id);
    } catch (error) {
      console.error('Error toggling paid:', error);
    }
  };

  const handleCopyRecurring = async () => {
    // Copiar del mes anterior al mes actual
    let sourceMonth = selectedMonth - 1;
    let sourceYear = selectedYear;
    if (sourceMonth === 0) {
      sourceMonth = 12;
      sourceYear = selectedYear - 1;
    }

    try {
      await copyRecurring.mutateAsync({
        source_month: sourceMonth,
        source_year: sourceYear,
        target_month: selectedMonth,
        target_year: selectedYear,
      });
      setShowCopyModal(false);
    } catch (error) {
      console.error('Error copying expenses:', error);
    }
  };

  // Group expenses by category for display
  const expensesByCategory = useMemo(() => {
    const grouped: Record<string, FixedExpense[]> = {};
    expenses.forEach((expense) => {
      const catName = expense.category?.name || 'Sin categoría';
      if (!grouped[catName]) {
        grouped[catName] = [];
      }
      grouped[catName].push(expense);
    });
    return grouped;
  }, [expenses]);

  // Determine result status
  const netResult = summary?.net_result || 0;
  const isPositive = netResult >= 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-taupe-900">Finanzas</h1>
          <p className="text-sm text-taupe-500">Control de gastos fijos y rentabilidad mensual</p>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-4 py-2 bg-white rounded-lg border border-taupe-200 text-center min-w-[160px] capitalize">
            <span className="font-medium text-taupe-800">
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={goToCurrentMonth} title="Ir al mes actual">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {loadingSummary ? (
        <div className="text-center py-4 text-taupe-500">Cargando resumen...</div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Ingresos (Ventas) */}
          <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold text-taupe-500">Ventas del Mes</p>
                <p className="text-2xl font-bold text-taupe-900 mt-1.5">
                  {formatPrice(summary.total_income)}
                </p>
              </div>
              <div className="p-2.5 bg-emerald-50 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </div>

          {/* Costo Mercadería Vendida (COGS) */}
          <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold text-taupe-500">Costo Mercadería</p>
                <p className="text-2xl font-bold text-taupe-900 mt-1.5">
                  {formatPrice(summary.total_cost)}
                </p>
                <p className="text-xs text-taupe-500 mt-1">
                  Lo que costó lo vendido este mes
                </p>
                {summary.total_variable_expenses > 0 && (
                  <p className="text-xs text-cream-700 mt-1 font-medium">
                    + {formatPrice(summary.total_variable_expenses)} gastos op.
                  </p>
                )}
              </div>
              <div className="p-2.5 bg-cream-100 rounded-lg">
                <TrendingDown className="h-5 w-5 text-cream-700" />
              </div>
            </div>
          </div>

          {/* Gastos Fijos */}
          <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold text-taupe-500">Gastos Fijos</p>
                <p className="text-2xl font-bold text-taupe-900 mt-1.5">
                  {formatPrice(summary.total_fixed_expenses)}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className="text-emerald-600 font-medium">
                    Pagado: {formatPrice(summary.fixed_expenses_paid)}
                  </span>
                  {summary.fixed_expenses_pending > 0 && (
                    <span className="text-cream-700 font-medium">
                      Pendiente: {formatPrice(summary.fixed_expenses_pending)}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-2.5 bg-rose-50 rounded-lg">
                <TrendingDown className="h-5 w-5 text-rose-600" />
              </div>
            </div>
          </div>

          {/* Resultado Neto */}
          <div
            className={clsx(
              'rounded-xl shadow-sm border p-5',
              isPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200',
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={clsx(
                    'text-xs uppercase tracking-wide font-semibold',
                    isPositive ? 'text-emerald-700' : 'text-rose-700',
                  )}
                >
                  Resultado Neto
                </p>
                <p
                  className={clsx(
                    'text-2xl font-bold mt-1.5',
                    isPositive ? 'text-emerald-700' : 'text-rose-700',
                  )}
                >
                  {formatPrice(netResult)}
                </p>
                <p
                  className={clsx(
                    'text-xs mt-1 font-medium',
                    isPositive ? 'text-emerald-600' : 'text-rose-600',
                  )}
                >
                  {isPositive ? 'Ganancia' : 'Pérdida'} del mes
                </p>
              </div>
              <div
                className={clsx(
                  'p-2.5 rounded-lg',
                  isPositive ? 'bg-emerald-100' : 'bg-rose-100',
                )}
              >
                <PiggyBank
                  className={clsx('h-5 w-5', isPositive ? 'text-emerald-700' : 'text-rose-700')}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Flujo de Caja */}
      {cashFlow ? (
        <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
          <h3 className="font-semibold text-taupe-900 mb-4">Flujo de Caja</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-taupe-600">Cobros (ventas)</span>
              <span className="text-sm font-semibold text-emerald-700">+{formatPrice(cashFlow.total_income)}</span>
            </div>
            {summary && summary.total_cost > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-600 inline-flex items-center gap-1">
                  Costo mercadería vendida
                  <span className="text-[10px] text-taupe-400 italic">(no es salida de caja real, ya pagaste al proveedor)</span>
                </span>
                <span className="text-sm font-semibold text-rose-600">-{formatPrice(summary.total_cost)}</span>
              </div>
            ) : null}
            {cashFlow.supplier_payments > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-600">Pagos a proveedores</span>
                <span className="text-sm font-semibold text-rose-600">-{formatPrice(cashFlow.supplier_payments)}</span>
              </div>
            ) : null}
            {cashFlow.fixed_expenses_paid > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-600">Gastos fijos pagados</span>
                <span className="text-sm font-semibold text-rose-600">-{formatPrice(cashFlow.fixed_expenses_paid)}</span>
              </div>
            ) : null}
            {cashFlow.operational_expenses > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-600">Gastos operativos</span>
                <span className="text-sm font-semibold text-rose-600">-{formatPrice(cashFlow.operational_expenses)}</span>
              </div>
            ) : null}
            {cashFlow.salary_expenses > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-600">Sueldos</span>
                <span className="text-sm font-semibold text-rose-600">-{formatPrice(cashFlow.salary_expenses)}</span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-3 mt-1 border-t border-taupe-200">
              <span className="text-sm font-bold text-taupe-900">Resultado operativo</span>
              <span className={clsx(
                'text-base font-bold',
                cashFlow.operating_cash_flow >= 0 ? 'text-emerald-700' : 'text-rose-700',
              )}>
                {formatPrice(cashFlow.operating_cash_flow)}
              </span>
            </div>
            {cashFlow.withdrawals > 0 ? (
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-taupe-400">Retiros personales</span>
                <span className="text-sm font-medium text-taupe-400">-{formatPrice(cashFlow.withdrawals)}</span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-3 border-t border-dashed border-taupe-200">
              <span className="text-sm font-bold text-taupe-500">Flujo neto</span>
              <span className={clsx(
                'text-base font-bold',
                cashFlow.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-taupe-500',
              )}>
                {formatPrice(cashFlow.net_cash_flow)}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Expenses by Category Summary */}
      {summary && summary.expenses_by_category.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
          <h3 className="font-semibold text-taupe-900 mb-4">Gastos por Categoría</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {summary.expenses_by_category.map((cat) => (
              <div
                key={cat.category}
                className="p-3 rounded-lg bg-cream-50 border border-taupe-100"
              >
                <p className="text-sm font-medium text-taupe-700 truncate">{cat.category}</p>
                <p className="text-lg font-bold text-taupe-900">{formatPrice(cat.total)}</p>
                <p className="text-xs text-taupe-500">{cat.count} gasto(s)</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Desglose de Salidas de Caja */}
      {withdrawalBreakdown && withdrawalBreakdown.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-5">
          <h3 className="font-semibold text-taupe-900 mb-4">Salidas de Caja por Categoría</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {withdrawalBreakdown.map((cat) => (
              <div
                key={cat.category}
                className="p-3 rounded-lg bg-cream-50 border border-taupe-100"
              >
                <p className="text-sm font-medium text-taupe-700 truncate">{cat.label}</p>
                <p className="text-lg font-bold text-taupe-900">{formatPrice(cat.total)}</p>
                <p className="text-xs text-taupe-500">{cat.count} movimiento(s)</p>
              </div>
            ))}
          </div>

          {/* Detalle: cada movimiento con categoría, medio de pago y fecha */}
          {withdrawalsDetail && withdrawalsDetail.length > 0 && (
            <div className="mt-4 border-t border-taupe-100 pt-3">
              <p className="text-sm font-medium text-taupe-700 mb-1">Detalle de movimientos</p>
              <div className="divide-y divide-cream-100">
                {withdrawalsDetail.map((w) => (
                  <div key={w.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-taupe-800 truncate">
                        {WITHDRAWAL_CAT_LABELS[w.category] || w.category}
                        {w.recipient ? ` · ${w.recipient}` : ''}
                      </p>
                      <p className="text-xs text-taupe-500 truncate">
                        {WITHDRAWAL_PAY_LABELS[w.payment_method] || w.payment_method}
                        {w.description ? ` · ${w.description}` : ''}
                        {' · '}{new Date(w.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-red-600 whitespace-nowrap">-{formatPrice(w.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={openNewExpenseModal}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Gasto
        </Button>
        <Button variant="secondary" onClick={() => setShowCopyModal(true)}>
          <Copy className="h-4 w-4 mr-2" />
          Copiar Recurrentes
        </Button>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-5 border-b">
          <h3 className="font-semibold text-gray-900">
            Gastos de {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </h3>
        </div>

        {loadingExpenses ? (
          <div className="text-center py-8">Cargando gastos...</div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No hay gastos registrados para este mes</p>
            <Button variant="secondary" className="mt-4" onClick={openNewExpenseModal}>
              <Plus className="h-4 w-4 mr-2" />
              Agregar primer gasto
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {Object.entries(expensesByCategory).map(([categoryName, categoryExpenses]) => {
              const firstExpense = categoryExpenses[0];
              const categoryIcon = firstExpense?.category?.icon;
              const IconComponent = categoryIcon ? CATEGORY_ICONS[categoryIcon] : MoreHorizontal;
              const categoryColor = firstExpense?.category?.color || 'gray';

              return (
                <div key={categoryName} className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={clsx(
                        'p-1.5 rounded-lg',
                        CATEGORY_COLORS[categoryColor] || 'bg-gray-100 text-gray-800'
                      )}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <h4 className="font-medium text-gray-700">{categoryName}</h4>
                    <span className="text-sm text-gray-500">
                      ({categoryExpenses.length})
                    </span>
                  </div>

                  <div className="space-y-2 ml-8">
                    {categoryExpenses.map((expense) => (
                      <div
                        key={expense.id}
                        className={clsx(
                          'flex items-center justify-between p-3 rounded-lg border',
                          expense.is_paid
                            ? 'bg-green-50 border-green-100'
                            : 'bg-gray-50 border-gray-100'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleTogglePaid(expense.id)}
                            className={clsx(
                              'p-1 rounded-full transition-colors',
                              expense.is_paid
                                ? 'text-green-600 hover:bg-green-100'
                                : 'text-gray-400 hover:bg-gray-200'
                            )}
                            title={expense.is_paid ? 'Marcar como pendiente' : 'Marcar como pagado'}
                          >
                            {expense.is_paid ? (
                              <CheckCircle2 className="h-5 w-5" />
                            ) : (
                              <Clock className="h-5 w-5" />
                            )}
                          </button>

                          <div>
                            <p
                              className={clsx(
                                'font-medium',
                                expense.is_paid ? 'text-green-800' : 'text-gray-900'
                              )}
                            >
                              {expense.description || expense.category?.name || 'Sin descripción'}
                              {expense.is_recurring && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                  Recurrente
                                </span>
                              )}
                            </p>
                            {expense.notes && (
                              <p className="text-sm text-gray-500">{expense.notes}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span
                            className={clsx(
                              'font-bold',
                              expense.is_paid ? 'text-green-700' : 'text-gray-900'
                            )}
                          >
                            {formatPrice(expense.amount)}
                          </span>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditExpenseModal(expense)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Editar"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setDeleteExpenseId(expense.id);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Resumen Anual Acumulado */}
      <div className="bg-white rounded-xl shadow-sm border border-taupe-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-cream-100 to-taupe-100 rounded-lg">
            <PiggyBank className="h-6 w-6 text-taupe-700" />
          </div>
          <div>
            <h3 className="font-semibold text-taupe-900">Acumulado Anual {selectedYear}</h3>
            <p className="text-sm text-taupe-500">Resumen de todo el año hasta el mes actual</p>
          </div>
        </div>

        {/* Estado de carga */}
        {loadingYearly && (
          <div className="text-center py-8 text-taupe-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-taupe-500 mx-auto mb-2"></div>
            Cargando resumen anual...
          </div>
        )}

        {/* Estado de error */}
        {yearlyError && !loadingYearly && (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            Error al cargar el resumen anual
            <p className="text-xs text-gray-400 mt-1">{String(yearlyError)}</p>
          </div>
        )}

        {/* Datos cargados */}
        {yearlySummary && !loadingYearly && (
          <>
            {/* Barra visual de balance */}
            <div className="relative mt-6 mb-4">
              <div className="relative h-10 bg-taupe-100 rounded-full overflow-hidden border border-taupe-200">
                {(() => {
                  // total_profit puede ser NEGATIVO si los costos superan las ventas (perdida bruta).
                  // En ese caso, la perdida se suma del lado de los gastos para representarlo bien.
                  const rawProfit = Number(yearlySummary.total_profit ?? 0);
                  const expenses = Number(yearlySummary.total_expenses ?? 0);
                  const positiveProfit = Math.max(0, rawProfit);
                  const lossAmount = Math.max(0, -rawProfit);
                  const totalRed = expenses + lossAmount;
                  const totalAll = positiveProfit + totalRed;
                  let profitPercent = totalAll > 0 ? (positiveProfit / totalAll) * 100 : 0;
                  // Clamp leve para que el indicador siempre se vea dentro de la barra
                  profitPercent = Math.max(2, Math.min(98, profitPercent));
                  const isPositive = yearlySummary.net_result >= 0;

                  return (
                    <>
                      {/* Lado de ganancias */}
                      <div
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-700 ease-out"
                        style={{ width: `${profitPercent}%` }}
                      />

                      {/* Lado de gastos */}
                      <div
                        className="absolute right-0 top-0 h-full bg-gradient-to-l from-rose-500 to-rose-600 transition-all duration-700 ease-out"
                        style={{ width: `${100 - profitPercent}%` }}
                      />

                      {/* Indicador de balance en el medio */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 transition-all duration-700 ease-out z-10"
                        style={{
                          left: `${profitPercent}%`,
                          transform: 'translateX(-50%) translateY(-50%)',
                        }}
                      >
                        <div
                          className={clsx(
                            'w-11 h-11 rounded-full flex items-center justify-center shadow-md ring-4 ring-white transition-all',
                            isPositive ? 'bg-emerald-500' : 'bg-rose-500',
                          )}
                        >
                          {isPositive ? (
                            <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-white" strokeWidth={2.5} />
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Labels de la barra */}
              <div className="flex justify-between mt-3">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                    Ganancias
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-rose-700 uppercase tracking-wide">
                    Gastos
                  </span>
                  <TrendingDown className="w-4 h-4 text-rose-600" />
                </div>
              </div>
            </div>

          {/* Números del resumen anual - clickeables */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <button
              onClick={() => setShowYearlyDetailModal('ventas')}
              className="text-center p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer border border-transparent hover:border-emerald-200"
            >
              <p className="text-xs text-emerald-600 uppercase tracking-wide font-semibold">Ventas</p>
              <p className="text-lg font-bold text-emerald-700">{formatPrice(yearlySummary.total_income)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('costo')}
              className="text-center p-3 bg-cream-100 rounded-lg hover:bg-cream-200 transition-colors cursor-pointer border border-transparent hover:border-cream-300"
            >
              <p className="text-xs text-cream-700 uppercase tracking-wide font-semibold">Gastos Variables</p>
              <p className="text-lg font-bold text-cream-800">{formatPrice(yearlySummary.total_variable_expenses)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('gastos')}
              className="text-center p-3 bg-rose-50 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer border border-transparent hover:border-rose-200"
            >
              <p className="text-xs text-rose-600 uppercase tracking-wide font-semibold">Gastos Fijos</p>
              <p className="text-lg font-bold text-rose-700">{formatPrice(yearlySummary.total_expenses)}</p>
            </button>
            <button
              onClick={() => setShowYearlyDetailModal('resultado')}
              className={clsx(
                'text-center p-3 rounded-lg transition-colors cursor-pointer border border-transparent',
                yearlySummary.net_result >= 0
                  ? 'bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-200'
                  : 'bg-rose-50 hover:bg-rose-100 hover:border-rose-200'
              )}
            >
              <p className={clsx(
                'text-xs uppercase tracking-wide',
                yearlySummary.net_result >= 0 ? 'text-emerald-600' : 'text-rose-600'
              )}>
                Resultado Neto
              </p>
              <p className={clsx(
                'text-lg font-bold',
                yearlySummary.net_result >= 0 ? 'text-emerald-700' : 'text-rose-700'
              )}>
                {formatPrice(yearlySummary.net_result)}
              </p>
            </button>
          </div>

          </>
        )}
      </div>

      {/* Create/Edit Expense Modal */}
      <Modal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          resetForm();
        }}
        title={editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
      >
        <div className="space-y-4">
          <Select
            label="Categoría"
            value={formCategoryId}
            onChange={(e) => setFormCategoryId(e.target.value)}
            options={[
              { value: '', label: 'Sin categoría' },
              ...categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
          />

          <Input
            label="Descripción (opcional)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Ej: Alquiler local"
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Monto <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={formAmount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                placeholder="0,00"
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
              />
            </div>
          </div>

          <Input
            label="Notas (opcional)"
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            placeholder="Notas adicionales..."
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formIsRecurring}
              onChange={(e) => setFormIsRecurring(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Gasto recurrente (se puede copiar al siguiente mes)
            </span>
          </label>

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowExpenseModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveExpense}
              disabled={!formAmount || createExpense.isPending || updateExpense.isPending}
              className="flex-1"
            >
              {createExpense.isPending || updateExpense.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteExpenseId(null);
        }}
        title="Eliminar Gasto"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteExpenseId(null);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteExpense}
              disabled={deleteExpense.isPending}
              className="flex-1"
            >
              {deleteExpense.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Copy Recurring Modal */}
      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        title="Copiar Gastos Recurrentes"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Esta acción copiará todos los gastos marcados como "recurrentes" del mes anterior a{' '}
            <strong>
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
            </strong>
            .
          </p>
          <p className="text-sm text-gray-500">
            Los gastos que ya existan en el mes actual (por descripción) no se duplicarán.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowCopyModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCopyRecurring}
              disabled={copyRecurring.isPending}
              className="flex-1"
            >
              {copyRecurring.isPending ? 'Copiando...' : 'Copiar Gastos'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Detalle Resumen Anual */}
      <Modal
        isOpen={showYearlyDetailModal !== null}
        onClose={() => setShowYearlyDetailModal(null)}
        title={
          showYearlyDetailModal === 'ventas' ? `Detalle de Ventas ${selectedYear}` :
          showYearlyDetailModal === 'costo' ? `Detalle de Gastos Variables ${selectedYear}` :
          showYearlyDetailModal === 'gastos' ? `Detalle de Gastos Fijos ${selectedYear}` :
          `Detalle de Resultado Neto ${selectedYear}`
        }
        size="xl"
      >
        {yearlySummary && (
          <div className="space-y-4">
            {/* Resumen mensual - siempre visible */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Resumen por Mes</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {showYearlyDetailModal === 'ventas' && 'Ventas'}
                        {showYearlyDetailModal === 'costo' && 'Gastos Variables'}
                        {showYearlyDetailModal === 'gastos' && 'Gastos Fijos'}
                        {showYearlyDetailModal === 'resultado' && 'Resultado Neto'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {yearlySummary.months.map((month) => {
                      const value = showYearlyDetailModal === 'ventas' ? month.total_income :
                                    showYearlyDetailModal === 'costo' ? month.total_variable_expenses :
                                    showYearlyDetailModal === 'gastos' ? month.total_expenses :
                                    month.net_result;
                      const isNegative = value < 0;

                      return (
                        <tr key={month.month} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {MONTH_NAMES[month.month - 1]}
                          </td>
                          <td className={clsx(
                            'px-4 py-3 text-sm font-medium text-right',
                            showYearlyDetailModal === 'ventas' && 'text-emerald-700',
                            showYearlyDetailModal === 'costo' && 'text-cream-800',
                            showYearlyDetailModal === 'gastos' && 'text-rose-700',
                            showYearlyDetailModal === 'resultado' && (isNegative ? 'text-rose-700' : 'text-emerald-700')
                          )}>
                            {formatPrice(value)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                      <td className={clsx(
                        'px-4 py-3 text-sm font-bold text-right',
                        showYearlyDetailModal === 'ventas' && 'text-emerald-700',
                        showYearlyDetailModal === 'costo' && 'text-cream-800',
                        showYearlyDetailModal === 'gastos' && 'text-rose-700',
                        showYearlyDetailModal === 'resultado' && (yearlySummary.net_result < 0 ? 'text-rose-700' : 'text-emerald-700')
                      )}>
                        {formatPrice(
                          showYearlyDetailModal === 'ventas' ? yearlySummary.total_income :
                          showYearlyDetailModal === 'costo' ? yearlySummary.total_variable_expenses :
                          showYearlyDetailModal === 'gastos' ? yearlySummary.total_expenses :
                          yearlySummary.net_result
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Desglose de Ventas */}
            {showYearlyDetailModal === 'ventas' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">Desglose de Ventas</h4>
                {loadingSales ? (
                  <div className="text-center py-4 text-gray-500">Cargando ventas...</div>
                ) : salesBreakdown ? (
                  <div className="space-y-4">
                    {/* Por método de pago */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-xs text-green-600">Efectivo</p>
                        <p className="text-sm font-bold text-green-700">
                          {formatPrice(salesBreakdown.by_payment_method.cash || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-blue-600">Tarjeta</p>
                        <p className="text-sm font-bold text-blue-700">
                          {formatPrice(salesBreakdown.by_payment_method.card || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-xs text-purple-600">Transferencia</p>
                        <p className="text-sm font-bold text-purple-700">
                          {formatPrice(salesBreakdown.by_payment_method.transfer || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-600">Total Ventas</p>
                        <p className="text-sm font-bold text-gray-700">{salesBreakdown.sales_count}</p>
                      </div>
                    </div>

                    {/* Lista de ventas (últimas 20) */}
                    <div className="max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Items</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pago</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {salesBreakdown.sales.slice(0, 50).map((sale) => (
                            <tr key={sale.sale_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-600">
                                {new Date(sale.date).toLocaleDateString('es-AR')}
                              </td>
                              <td className="px-3 py-2 text-gray-900">
                                {sale.customer_name || '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600">
                                {sale.items_count}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {PAYMENT_METHOD_LABELS[sale.payment_method as keyof typeof PAYMENT_METHOD_LABELS] || sale.payment_method}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-blue-700">
                                {formatPrice(sale.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {salesBreakdown.sales.length > 50 && (
                        <p className="text-center text-xs text-gray-500 py-2">
                          Mostrando 50 de {salesBreakdown.sales.length} ventas
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Desglose de Costo de Productos */}
            {showYearlyDetailModal === 'costo' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2">Desglose por Producto</h4>
                {loadingProductCost ? (
                  <div className="text-center py-4 text-gray-500">Cargando productos...</div>
                ) : productCostBreakdown ? (
                  <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Cant. Vendida</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Costo Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Costo Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {productCostBreakdown.products.map((product) => (
                          <tr key={product.product_id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-900">{product.product_name}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{product.quantity_sold}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatPrice(product.unit_cost)}</td>
                            <td className="px-3 py-2 text-right font-medium text-cream-800">{formatPrice(product.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-sm font-bold text-gray-900">TOTAL COSTO</td>
                          <td className="px-3 py-2 text-right font-bold text-cream-800">
                            {formatPrice(productCostBreakdown.total_cost)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    {productCostBreakdown.products.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No hay productos vendidos en este período</p>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Explicación del cálculo para Resultado Neto */}
            {showYearlyDetailModal === 'resultado' && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-700 mb-3">Cálculo del Resultado Neto</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas totales</span>
                    <span className="font-medium text-blue-700">{formatPrice(yearlySummary.total_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Costo de productos</span>
                    <span className="font-medium text-gray-500">-{formatPrice(yearlySummary.total_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Gastos variables (salidas de caja)</span>
                    <span className="font-medium text-cream-800">-{formatPrice(yearlySummary.total_variable_expenses)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">= Ganancia bruta</span>
                    <span className="font-medium text-green-700">{formatPrice(yearlySummary.total_profit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">- Gastos fijos</span>
                    <span className="font-medium text-red-700">-{formatPrice(yearlySummary.total_expenses)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-base">
                    <span className="font-bold text-gray-900">= Resultado Neto</span>
                    <span className={clsx(
                      'font-bold',
                      yearlySummary.net_result >= 0 ? 'text-emerald-700' : 'text-rose-700'
                    )}>
                      {formatPrice(yearlySummary.net_result)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={() => setShowYearlyDetailModal(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
